import { Injectable } from '@angular/core';
import {
  DocumentSnapshot,
  FieldValue,
  Firestore,
  QueryDocumentSnapshot,
  QuerySnapshot,
  Timestamp,
  arrayRemove,
  arrayUnion,
  collection,
  collectionData,
  deleteDoc,
  deleteField,
  doc,
  docData,
  documentId,
  endAt,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  startAfter,
  startAt,
  updateDoc,
  where,
} from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  ChannelWithDocId,
  getChannel
} from '@yoimo/client-sdk/channels';
import { Branding, Channel, CustomAttributes, ChannelNavigation, Page, PageModuleList, PageTab, Ticket, Plan } from '@yoimo/interfaces';
import { chunk, uniq } from 'lodash';
import { BehaviorSubject, Observable, Subject, from, lastValueFrom, of } from 'rxjs';
import { auditTime, first, map, take, tap } from 'rxjs/operators';
import {
  ChannelTabs,
  Tabs,
  timeDropDown,
} from '../home/channels-view/channels-view.component';
import { Club } from '../interfaces/clubs';
import { Sport } from '../interfaces/sports';
import { UserData } from '../interfaces/user';
import { CountryCode } from './country-code.service';
import { ArchivedStatus } from '../home/stream-individual-view/stream-individual-view.component';
import { ArchivedInfo } from './livestreamGroups.service';

export interface FilterMenu {
  showName: boolean;
  showTime: boolean;
  showCountry: boolean;
  createdFrom: boolean;
  channelType?: boolean;
}

export interface FilterInput {
  timeValue: number;
  clubName: string;
  country?: CountryCode;
  createdFrom: string;
  channelType?: string;
}

export interface NewChannel {
  fullName: string;
  country: string;
  defaultCurrency: string;
  name: string;
  sportDocId: string;
  adminEmails: string[];
  dialCode: string;
  phoneNumber: string;
  description: string;
  isExclusive: boolean;
  coverImageUrl: string;
  imageUrl: string;
  docId: string;
  createdFrom: string;
  channelId?: string;
  isMasterChannel?: boolean;
  isOTTCreation?: boolean;
}

export interface EditChannel extends NewChannel{
  sportDocIds: string[]
  adminDocIds: string[]
  users: Record<string, UserData>
  logoUrl?: string;
  sport?: Sport;
  channelDetails?: ChannelDetails;
  defaultAdCuePoints: DefaultAdCuePoints[];
  watermark: Watermark;
}

export interface Watermark {
  enabled: boolean;
  imageUrl: string;
  watermarkFile?: File;
}

export interface HoldingImage {
  enabled: boolean;
  imageUrls: any[];
}

export interface DefaultAdCuePoints {
  cueId: string;
  time: 'START' | 'END';
  tag?: string;
}

export interface ChannelDetails {
  channelId: string | null;
  isMasterChannel: boolean;
  defaultTags?: string[] | FieldValue;
  defaultEventId?: string | null | FieldValue;
  defaultAttribute?: CustomAttributes | FieldValue;
  // allowEmptyEventSelection?: boolean;
  allowUnlockedContent?: boolean;
}

export enum ChannelType {
  NEW_CHANNEL = 'NEW_CHANNEL',
  ATTACH_CHANNEL = 'ATTACH_CHANNEL',
}

export const defaultNamedTagDropDownValues = [
  { key: 'Event', value: 'EVENT' },
  { key: 'Athlete', value: 'ATHLETE' },
  { key: 'Place', value: 'PLACE' },
  { key: 'Tournament', value: 'TOURNAMENT' },
  { key: 'Team', value: 'TEAM' },
  { key: 'Season', value: 'SEASON' },
];

export interface ClubCoverPicture {
  url: string;
  file?: File;
}

export interface Stream extends CustomAttributes {
  archived?: boolean;
  archivedInfo?: ArchivedInfo;
  archivedReason?: ArchivedStatus;
  availableForBundle?: boolean;
  availableForVod?: boolean;
  availableForDownload: boolean;
  availableForOTT: boolean;
  bulkImportDocId?: string;
  bundlePrice?: number;
  bundlePriceStatus?: number;
  buyerDocIds?: string[];
  clubDocId: string;
  clubName: string;
  createdTime: Date | Timestamp;
  delayDuration?: number;
  description: string;
  docId?: string;
  duration: number;
  eventId?: string;
  force_currency: string;
  forceCurrency?: number;
  isClip?: boolean;
  isDelayed: boolean;
  isPublic: boolean;
  isReserved: boolean;
  isTest?: boolean;
  livestreamPriceStatus?: number;
  mailRtmpSettingsTo?: string[];
  name: string;
  ownerDocId: string;
  price?: number;
  published: boolean;
  reserved?: string;
  reservedInput?: any;
  rtmpUrl?: string;
  srtUrl?: string;
  restrictedTo?: string[] | { exclude: string[] };
  scheduledStartTime: Date | Timestamp;
  scheduledStopTime: Date | Timestamp;
  selectedEvent?: Record<string, string>;
  selectedTeam: Record<string, string>;
  sport?: Record<string, string>;
  sportyDocId: string;
  sportyName?: string;
  streamingMethod: string;
  streamState: 'SCHEDULED' | 'LIVE' | 'FINISHED' | null;
  tags: string[];
  teamA_Name?: string;
  teamB_Name?: string;
  teamDocId?: string;
  teamName?: string;
  ticketsSold?: {
    bundle: number;
    ppv: number;
    vod: number;
  };
  unlocked: boolean;
  useIotScheduling?: boolean;
  useStreamerApp?: boolean;
  vodPrice?: number;
  vodPriceStatus?: number;
}

export type TabbedModulesErrors = {
  [tabId: string]: {
    name: boolean;
    order: boolean;
    modules: boolean[];
    icon: boolean;
  };
};

export type InvalidPageError = {
  pageError: TabbedModulesErrors | boolean[];
  type: 'tabbed' | 'module';
};

export type IconValuesType = {
  [tabId: string]: {
    iconFile: File | null;
    iconUrl?: string;
    iconBuffer?: string;
    invalidUrl: boolean;
  };
};

export type SourceDocuments = {
  videoSourceDocs: Record<string, string>[];
  tagSourceDocs: Record<string, string>[];
  eventSourceDocs: Record<string, string>[];
};

interface PageTabWithFlexibleOrder extends Omit<PageTab, 'order'> {
  order: string | number;
}

export interface TabbedPageModules {
  [tabId: string]: PageTabWithFlexibleOrder;
}

export type SubscriptionPlan = Pick<Plan, 'name' | 'available' | 'archived'> & {
  docId: string;
};

export interface PlansAndTicketsResponse {
  subscriptions: SubscriptionPlan[];
  validTickets: Pick<Ticket, 'id' | 'name' | 'available' | 'prices'>[];
}

export interface DisplayPlan {
  docId: string;
  name: string;
  type: 'SUBSCRIPTION' | 'TICKET';
  priceLabel?: string;
}

export const TRANSIENT_SCHEDULING_STATES = [
  'SCHEDULING_QUEUED',
  'STACK_CREATING',
  'STACK_CREATED',
  'STACK_READY',
  'STACK_DIDNT_RECEIVE_INITIAL_DATA',
  'STACK_RECEIVING_DATA',
  'STACK_STOPPED_RECEIVING_DATA',
  'STACK_STOPPING',
];

export const ALWAYS_EDITABLE_FIELDS = [
  'name',
  'description',
  'restrictedTo'
];

export const EDITABLE_DURING_QUEUED_FIELDS = [
  'selectedEvent',
  'tags',
  'attributes',
  'ownerDocId',
  'duration',
  'scheduledStartTime',
  'unlocked',
  'streamingMethod',
  'reservedInput',
  'mailRtmpSettingsTo'
];

export const EDITABLE_WHEN_STOPPED_FIELDS = [
  'selectedEvent',
  'tags',
  'attributes',
  'unlocked',
  'published',
  'availableForDownload'
];

export const STACK_TRANSITION_CONTROLLED_FIELDS = [
  'selectedEvent',
  'tags',
  'attributes',
  'duration',
  'streamingMethod',
  'reservedInput',
  'mailRtmpSettingsTo'
];

export const DISABLED_WHEN_STOPPED_OR_STOPPING_FIELDS = [
  'scheduledStartTime',
  'ownerDocId'
];

export const TIME_WINDOW_CONTROLLED_FIELDS = [
  'scheduledStartTime',
  'duration',
  'streamingMethod',
  'reservedInput'
];

export const RAW_START_TIME_CONTROLLED_FIELDS = [
  'scheduledStartTime',
  'duration',
  'ownerDocId'
];

export const UNAFFECTED_BY_STACK_TRANSITION_FIELDS = [
  'published'
];

@Injectable({
  providedIn: 'root',
})
export class ChannelsService {
  tabId: Tabs;
  tabIds = Tabs;
  channelTabId: ChannelTabs;
  channelTabIds = ChannelTabs;
  filterMenu: FilterMenu = {
    showTime: false,
    showName: false,
    showCountry: false,
    createdFrom: false,
    channelType: false,
  };
  filterInput: Partial<FilterInput> = {
    clubName: '',
    country: { country: '', code: '' },
    createdFrom: 'All',
    channelType: 'All',
  };
  endOfClub = false;
  private readonly isChannelInfo = new BehaviorSubject<boolean>(false);
  private readonly isEventPage = new BehaviorSubject<boolean>(false);
  public shareBoolean = this.isChannelInfo.asObservable();
  public eventPageBody = this.isEventPage.asObservable();

  private readonly isLoader = new BehaviorSubject<boolean>(false);
  public shareLoader = this.isLoader.asObservable();

  isFormHasValues = false;
  editDocId = '';
  isEdit = false;
  isHomeTabEdit = false;
  fromChannelInfo = false;
  selectedTime: timeDropDown | null | undefined;
  logoImage = '';
  activeHomeTab = 0;
  activePageTab = 0;
  modules!: PageModuleList | TabbedPageModules;
  private readonly invalidPageModule = new Subject<InvalidPageError>();
  tabbedPagesErrors: TabbedModulesErrors = {};
  iconValues: IconValuesType = {};
  // --- Dropdown shared state ---
  ddName = '';
  ddOpen = false;
  dropdownListener: EventListener = () => {};

  private readonly sourceDocs = new BehaviorSubject<SourceDocuments>({
    videoSourceDocs: [],
    tagSourceDocs: [],
    eventSourceDocs: [],
  });
  sourceDocs$ = this.sourceDocs.asObservable();

  constructor(private readonly firestore: Firestore, private readonly functions: Functions) {
    this.tabId = this.tabIds.enabled;
    this.channelTabId = this.channelTabIds.CLUBS;
  }

  initDropdownListener(closeDropdownFn: (e: Event) => void): void {
    this.dropdownListener = (e: Event) => closeDropdownFn(e);
  }

  dropDownDivOpen(
    dropName: string,
    index: number,
    isEventCleared: boolean,
    resetFn: () => void,
    ele?: HTMLInputElement,
  ): void {
    if (
      dropName === 'videoListSource' + index ||
      dropName === 'videoListtagSource' + index
    ) {
      window.addEventListener('click', this.dropdownListener, false);
    }

    if (
      dropName === 'DateRange' + index + 'from' ||
      dropName === 'DateRange' + index + 'to'
    ) {
      window.addEventListener('click', this.dropdownListener, false);
    }

    if (isEventCleared) {
      window.removeEventListener('click', this.dropdownListener, false);
      resetFn();
      ele?.blur();
      return;
    }

    this.ddName = dropName;
    this.ddOpen = true;

    if (dropName === '') {
      window.removeEventListener('click', this.dropdownListener, false);
      resetFn();
    }
  }

  closeDropdown(evt: Event, resetFn: () => void): void {
    const target = evt.target as HTMLElement;
    const classList = new Set([
      ...Array.from(target.classList),
      ...Array.from(target.parentElement?.classList ?? []),
    ]);

    const skippedClasses = new Set([
      'multiSelectInput',
      'multi-select-checkbox',
      'mat-checkbox-layout',
      'currency-select-div',
    ]);

    if ([...skippedClasses].some((cls) => classList.has(cls))) {
      return;
    }

    this.dropDownDivOpen('', 0, false, resetFn);
  }

  getClubById(id: string): Observable<Club> {
    const clubDocRef = doc(this.firestore, 'clubs', id);
    return docData(clubDocRef, { idField: 'docId' }) as Observable<Club>;
  }

  getClubByIdPromise(id: string) {
    const clubDocRef = doc(this.firestore, 'clubs', id);
    return getDoc(clubDocRef);
  }

  deleteClub(clubId: string) {
    const clubDocRef = doc(this.firestore, 'clubs', clubId);
    return deleteDoc(clubDocRef);
  }

  retrieveClub(clubId: string) {
    const archiveDocRef = doc(this.firestore, 'archive', clubId);
    return deleteDoc(archiveDocRef);
  }

  setChannelInfo() {
    this.isChannelInfo.next(true);
  }

  setEventPage() {
    this.isEventPage.next(true);
  }

  resetChannelInfo() {
    this.isChannelInfo.next(false);
  }

  resetEventPageInfo() {
    this.isEventPage.next(false);
  }

  setLoaderStatus() {
    this.isLoader.next(true);
  }

  resetLoaderStatus() {
    this.isLoader.next(false);
  }

  getArchivedById<T>(id: string): Observable<T> {
    const archiveDocRef = doc(this.firestore, 'archive', id);
    return docData(archiveDocRef) as Observable<T>;
  }

  checkLiveOrArchive(id: string): Observable<any> {
    const clubDocRef = doc(this.firestore, 'clubs', id);
    return from(getDoc(clubDocRef));
  }

  getClubByName(collectionName: string, name: string): Observable<any[]> {
    const collRef = collection(this.firestore, collectionName);
    const q = query(
      collRef,
      orderBy('name'),
      startAt(name.toUpperCase()),
      endAt(name.toLowerCase() + '\uf8ff')
    );
    return collectionData(q, { idField: 'id' }).pipe(auditTime(1400));
  }

  getClubByType(collectionName: string): Observable<any[]> {
    const collRef = collection(this.firestore, collectionName);
    const q = query(collRef, orderBy('channelDetails'));
    return collectionData(q, { idField: 'id' }).pipe(auditTime(1400));
  }

  getClubsDataAll(collectionName: string): Observable<any[]> {
    const collRef = collection(this.firestore, collectionName);
    const q = query(collRef, orderBy('name'));
    return collectionData(q, { idField: 'id' }).pipe(auditTime(1400));
  }

  getClubByCountry(collectionName: string, country: string): Observable<any[]> {
    const collRef = collection(this.firestore, collectionName);
    const q = query(collRef, where('country', '==', country));
    return collectionData(q, { idField: 'id' }).pipe(auditTime(2000));
  }

  getClubByCreated(collectionName: string): Observable<any[]> {
    const collRef = collection(this.firestore, collectionName);
    const q = query(collRef, orderBy('createdTime'));
    return collectionData(q, { idField: 'id' }).pipe(auditTime(2000));
  }

  getBatch(collectionName: string, lastSeen: string): Observable<any> {
    const collRef = collection(this.firestore, collectionName);
    const q = query(collRef, orderBy('name'), startAfter(lastSeen), limit(50));

    return from(getDocs(q)).pipe(
      auditTime(400),
      take(1),
      tap((snapshot) => {
        if (!snapshot.docs.length) {
          this.endOfClub = true;
        }
      }),
      map((snapshot) => snapshot.docs.reduce((acc, doc) => {
          const id = doc.id;
          const data = doc.data();
          return { ...acc, [id]: { id, ...data } };
        }, {})
      ));
  }

  async getClubsByIdArray<T>(values: string[], collectionName: string): Promise<T[]> {
    const docIdPath = documentId();
    const results = await Promise.all(
      chunk(values, 10).map(slice => {
        const collRef = collection(this.firestore, collectionName);
        const q = query(collRef, where(docIdPath, 'in', slice));
        return getDocs(q);
      })
    );

    return results.reduce((acc: any, batchResult) => [...acc, ...(batchResult.docs)], []);
  }

  removeAdminFromChannel(
    docId: string,
    adminDocIds: string[]
  ): Observable<any> {
    const clubDocRef = doc(this.firestore, 'clubs', docId);
    return from(
      updateDoc(clubDocRef, {
        adminDocIds: arrayRemove(...adminDocIds),
      })
    );
  }

  removeTrainerFromChannel(
    docId: string,
    trainerDocIds: string[],
    teamDocIds: string[]
  ): Observable<boolean> {
    let result = false;
    try {
      for (const teamId of teamDocIds){
        const teamDocRef = doc(this.firestore, 'teams', teamId);
        updateDoc(teamDocRef, {
          trainerDocIds: arrayRemove(...trainerDocIds),
        });
      }
    } catch (error) {
      if (error) {result = true;}
    }
    return of(result);
  }

  removeStreamerFromChannel(
    docId: string,
    streamerDocIds: string[]
  ): Observable<any> {
    const clubDocRef = doc(this.firestore, 'clubs', docId);
    return from(
      updateDoc(clubDocRef, {
        streamerDocIds: arrayRemove(...streamerDocIds),
      })
    );
  }

  removeMemberFromChannel(
    clubDocId: string,
    membersDocIds: string[]
  ): Observable<any> {
    const removeMembersCallable = httpsCallable(
      this.functions,
      'users-removeMembersOfClub'
    );
    return from(
      removeMembersCallable({ clubDocId, userDocIds: membersDocIds })
    ).pipe(map((response: any) => response.data.success));
  }

  getClubInfo(collectionName: string, clubDocId: string): Observable<Club> {
    const clubDocRef = doc(this.firestore, collectionName, clubDocId);
    return docData(clubDocRef, { idField: 'docId' }) as Observable<Club>;
  }

  async createChannel(newChannel: NewChannel) {
    const registerCallable = httpsCallable(this.functions, 'club-register');
    const result = await registerCallable({ club: newChannel });
    return result.data;
  }

  async updateChannel(channelData: Partial<EditChannel>, docId: string) {
    const clubDocRef = doc(this.firestore, 'clubs', docId);
    return setDoc(clubDocRef, channelData, { merge: true });
  }

  async bulkDelete(bulkImportDocId: string) {
    const deleteBulkImport = httpsCallable(
      this.functions,
      'livestreams-deleteBulkImport'
    );
    return (await deleteBulkImport({ bulkImportDocId })).data;
  }

  editClubLogo(docId: string, url: string) {
    const clubDocRef = doc(this.firestore, 'clubs', docId);
    return updateDoc(clubDocRef, { imageUrl: url });
  }

  editBrandingLogo(docId: string, url: string) {
    const channelDocRef = doc(this.firestore, 'channels', docId);
    return updateDoc(channelDocRef, { 'branding.logoImage': url });
  }

  updateHoldingImages(docId: string, obj: Record<string, any>) {
    const holdingImagesDocRef = doc(
      this.firestore,
      'clubs',
      docId,
      'holdingImages',
      'holdingImages'
    );
    return setDoc(holdingImagesDocRef, obj);
  }

  updateWatermark(docId: string, obj: Record<string, any>) {
    const watermarkDocRef = doc(
      this.firestore,
      'clubs',
      docId,
      'watermark',
      'watermark'
    );
    return setDoc(watermarkDocRef, obj);
  }

  async getHoldingImages(docId: string) {
    const holdingImagesDocRef = doc(
      this.firestore,
      'clubs',
      docId,
      'holdingImages',
      'holdingImages'
    );
    const docSnap = await getDoc(holdingImagesDocRef);
    return docSnap;
  }

  async getWatermark(docId: string) {
    const watermarkDocRef = doc(
      this.firestore,
      'clubs',
      docId,
      'watermark',
      'watermark'
    );
    const docSnap = await getDoc(watermarkDocRef);
    return docSnap;
  }

  async manageCueTag(
    docId: string,
    oldObj: Record<string, string>,
    newObj: Record<string, string>
  ) {
    const clubDocRef = doc(this.firestore, 'clubs', docId);
    await updateDoc(clubDocRef, {
      defaultAdCuePoints: arrayRemove(oldObj),
    });
    return updateDoc(clubDocRef, {
      defaultAdCuePoints: arrayUnion(newObj),
    });
  }

  updateArchivedChannel(email: string, docId: string) {
    const archiveDocRef = doc(this.firestore, 'archive', docId);
    return updateDoc(archiveDocRef, { unarchiveEmail: email });
  }

  updateNewChannel(docId: string, obj: Partial<Channel>){
    const channelDocRef = doc(this.firestore, 'channels', docId);
    return updateDoc(channelDocRef, obj);
  }

  addFavicon(channelDocId: string, url: string){
    const channelDocRef = doc(this.firestore, 'channels', channelDocId);
    return updateDoc(channelDocRef, {
      'branding.favicon' : url
    });
  }

  removeFavicon(docId: string){
    const channelDocRef = doc(this.firestore, 'channels', docId);

    return updateDoc(channelDocRef, {
      'branding.favicon' : deleteField()
    });
  }

  async updateNewChannelData(
    docId: string,
    obj: Partial<Page>,
    pagePropsToDelete: Partial<keyof Page>[] = []
  ): Promise<void> {
    const defaultData: Page[] = [
      {
        active: true,
        metaTags: [],
        modules: [],
        name: '',
        status: 'PUBLISHED',
      },
    ];

    const channelDocRef = doc(this.firestore, 'channels', docId);
    const channelSnap = await getDoc(channelDocRef);

    if (!channelSnap.exists()) {
      throw new Error('Document does not exist.');
    }

    const channelData = channelSnap.data() as Channel;
    const pages = channelData.pages?.length ? channelData.pages : defaultData;

    pages[0] = { ...pages[0], ...obj };
    for (const property of pagePropsToDelete) {
      if (pages[0][property]) {delete pages[0][property];}
    }

    return updateDoc(channelDocRef, { pages });
  }

  updateChannelBranding(docId: string, obj: Partial<Channel>) {
    const channelDocRef = doc(this.firestore, 'channels', docId);
    return setDoc(channelDocRef, { branding: obj }, { merge: true });
  }

  updateChannelNavigation(docId: string, obj: Partial<ChannelNavigation>) {
    const channelDocRef = doc(this.firestore, 'channels', docId);
    return setDoc(
      channelDocRef,
      {
        channelNavigation: obj.length ? obj : deleteField(),
      },
      { merge: true }
    );
  }

  updateBrandingBannerImages(docId: string, obj: Partial<Branding>) {
    const channelDocRef = doc(this.firestore, 'channels', docId);
    return setDoc(
      channelDocRef,
      { branding: { bannerImages: obj } },
      { merge: true }
    );
  }

  updateBrandingContactInfo(docId: string, isContactInfo: boolean, obj: Partial<Branding>) {
    const channelDocRef = doc(this.firestore, 'channels', docId);
    if (isContactInfo) {
      return updateDoc(channelDocRef, {
        'branding.contactInformation': deleteField(),
      });
    } else {
      return setDoc(
        channelDocRef,
        {
          branding: { contactInformation: obj },
        },
        { merge: true }
      );
    }
  }

  getLastLiveStream(channelId: string) {
    return new Promise((resolve, reject) => {
      const videosCollection = collection(
        this.firestore,
        'channels',
        channelId,
        'videos'
      );
      const q = query(videosCollection, orderBy('endTime', 'desc'), limit(1));

      const subscription = collectionData(q)
        .pipe(first())
        .subscribe({
          next: (querySnapshot) => {
            if (querySnapshot.length > 0) {
              const latestStream = querySnapshot[0];
              resolve(latestStream.endTime.toDate());
            } else {
              resolve('N/A');
            }
          },
          error: (error) => {
            reject(error);
          },
          complete: () => {
            subscription.unsubscribe();
          },
        });
    });
  }

  getNewChannelById(channelId: string): Observable<ChannelWithDocId | undefined> {
    return getChannel(this.firestore, channelId);
  }

  async getChannelById(channelId: string): Promise<ChannelWithDocId | undefined> {
    return lastValueFrom(getChannel(this.firestore, channelId));
  }

  async getOrganizationChannel<T>(docId: string, collectionName: string) {
    const docRef = doc(this.firestore, collectionName, docId);
    return getDoc(docRef) as Promise<DocumentSnapshot<T>>;
  }

  async createNewChannel(newChannel: Channel) {
    const createChannelCallable = httpsCallable(
      this.functions,
      'channel-createChannel'
    );
    return (await createChannelCallable(newChannel)).data;
  }

  getAttachedChannels(newChannelId: string){
    const clubsCollection = collection(this.firestore, 'clubs');
    const q = query(
      clubsCollection,
      where('channelDetails.channelId', '==', newChannelId)
    );
    return collectionData(q, { idField: 'docId' }).pipe(auditTime(1000));
  }

  async getMasterClub(newChannelId: string): Promise<string> {
    const clubsCollection = collection(this.firestore, 'clubs');
    const q = query(
      clubsCollection,
      where('channelDetails.channelId', '==', newChannelId),
      where('channelDetails.isMasterChannel', '==', true)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs[0]?.id;
  }

  async getMasterTeam(clubId: string): Promise<{ teamDocId: string; data: any } | null> {
    const teamsCollection = collection(this.firestore, 'teams');
    const q = query(
      teamsCollection,
      where('clubDocId', '==', clubId),
      where('isDefaultTeam', '==', true)
    );
    const snapshot = await getDocs(q);
    console.log(snapshot.docs);

    const teamDoc = snapshot.docs[0];
    if (!teamDoc) {
      return null;
    }

    return {
      teamDocId: teamDoc.id,
      data: teamDoc.data(),
    };
  }

  async getTeamName(teamId: string): Promise<string> {
    if (!teamId) { return ''; }
    const teamDocRef = doc(this.firestore, 'teams', teamId);
    const teamSnap = await getDoc(teamDocRef);
    return (teamSnap.data() as { name?: string })?.name || '';
  }

  async getAttachedChannelsPromise(newChannelId: string) {
    const clubsCollection = collection(this.firestore, 'clubs');
    const q = query(
      clubsCollection,
      where('channelDetails.channelId', '==', newChannelId)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ docId: doc.id, ...(doc.data() as Club) }));
  }

  splitTags(existingTags: string[], tags: string): string[] {
   let updatedTags = [];
   if (tags.includes(',')){
     updatedTags = tags.split(/\s*,\s*/);
   }
   else {
    updatedTags = [tags];
    }

    return uniq(
      updatedTags.filter(
        (tag) => tag.trim() !== '' && !existingTags.includes(tag.trim())
      )
    );
  }

  setModuleError(invalidPageError: InvalidPageError): void {
    if (invalidPageError.type == 'tabbed') {
      this.tabbedPagesErrors =
        invalidPageError.pageError as TabbedModulesErrors;
    }
    this.invalidPageModule.next(invalidPageError);
  }

  getModuleError(): Observable<InvalidPageError> {
    return this.invalidPageModule.asObservable();
  }

  updateSourceDocs(newDocs: Partial<SourceDocuments>) {
    const currentDocs = this.sourceDocs.getValue();
    this.sourceDocs.next({ ...currentDocs, ...newDocs });
  }

  async getCrewRestriction(payload: string | undefined) {
    const result = await httpsCallable(this.functions, 'club-getvideorestrictionsforcrew')({
      teamDocId: payload,
    });
    return result.data;
  }
}
